import React, { useState, useEffect, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "../App";
import AdminLayout from "../components/AdminLayout";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import FinancialIndexCards from "../components/FinancialIndexCards";
import { Separator } from "../components/ui/separator";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import {
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  ResponsiveContainer,
  Tooltip
} from "recharts";
import { 
  Download, 
  FileText, 
  ArrowLeft,
  TrendingUp,
  TrendingDown,
  Lightbulb,
  Users,
  Loader2,
  ClipboardList,
  Copy,
  Link as LinkIcon,
  Landmark,
  FileDown,
  Trash2
} from "lucide-react";

const REFERRAL_STATUS_META = {
  new: { label: "Novo", className: "bg-slate-100 text-slate-700" },
  assigned: { label: "Atribuído", className: "bg-amber-100 text-amber-800" },
  contacted: { label: "Contactado", className: "bg-purple-100 text-purple-800" },
  in_progress: { label: "Em andamento", className: "bg-sky-100 text-sky-800" },
  closed: { label: "Fechado", className: "bg-emerald-100 text-emerald-800" },
  completed: { label: "Concluído", className: "bg-emerald-100 text-emerald-800" },
  not_closed_contract: { label: "Não fechou contrato", className: "bg-rose-100 text-rose-800" },
};

const REFERRAL_SOURCE_LABELS = {
  client_request: "Solicitada pela empresa",
  manual_admin: "Direcionada pelo administrador",
  auto_critical: "Encaminhamento automático",
};

const AdminResults = () => {
  const { diagnosticId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [results, setResults] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [downloadingTxt, setDownloadingTxt] = useState(false);
  const [showAnswers, setShowAnswers] = useState(false);
  const [aiTips, setAiTips] = useState(null);
  const [loadingTips, setLoadingTips] = useState(false);
  const [selectedArea, setSelectedArea] = useState(null);
  const [showAreaDetail, setShowAreaDetail] = useState(false);
  const [consultants, setConsultants] = useState([]);
  const [assigning, setAssigning] = useState(false);
  const [referralArea, setReferralArea] = useState("");
  const [referralCity, setReferralCity] = useState("all");
  const [referralConsultant, setReferralConsultant] = useState("");
  const [financialAssessment, setFinancialAssessment] = useState(null);
  const [loadingFinancial, setLoadingFinancial] = useState(false);
  const [reanalyzingFinancial, setReanalyzingFinancial] = useState(false);
  const [accountData, setAccountData] = useState(null);
  const [showAccountDialog, setShowAccountDialog] = useState(false);
  const [savingAccount, setSavingAccount] = useState(false);
  const [accountForm, setAccountForm] = useState({
    company_name: "",
    company_cnpj: "",
    company_segment: "",
    company_size: "",
    user_name: "",
    user_email: "",
    user_password: ""
  });

  const fetchData = useCallback(async () => {
    setLoadingFinancial(true);
    try {
      const [resultsRes, diagnosticRes, questionsRes, consultantsRes, financialRes, accountRes] = await Promise.all([
        axios.get(`${API}/diagnostics/${diagnosticId}/results`, { withCredentials: true }),
        axios.get(`${API}/diagnostics/${diagnosticId}`, { withCredentials: true }),
        axios.get(`${API}/questions`, { withCredentials: true }),
        axios.get(`${API}/consultants?active_only=true`, { withCredentials: true }),
        axios.get(`${API}/diagnostics/${diagnosticId}/financial-assessment`, { withCredentials: true }).catch(() => ({ data: { exists: false } })),
        axios.get(`${API}/diagnostics/${diagnosticId}/account`, { withCredentials: true }).catch(() => ({ data: null }))
      ]);
      setResults(resultsRes.data);
      setAnswers(diagnosticRes.data.answers || []);
      setQuestions(questionsRes.data.questions || []);
      setConsultants(consultantsRes.data.consultants || []);
      setFinancialAssessment(financialRes.data?.exists ? financialRes.data : null);
      if (accountRes?.data?.company && accountRes?.data?.user) {
        setAccountData(accountRes.data);
        setAccountForm({
          company_name: accountRes.data.company.name || "",
          company_cnpj: accountRes.data.company.cnpj || "",
          company_segment: accountRes.data.company.segment || "",
          company_size: accountRes.data.company.size || "",
          user_name: accountRes.data.user.name || "",
          user_email: accountRes.data.user.email || "",
          user_password: ""
        });
      }

      const preferredArea = resultsRes.data?.critical_areas?.[0]?.area || "";
      if (preferredArea) {
        setReferralArea(preferredArea);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Erro ao carregar resultados");
      navigate("/admin");
    } finally {
      setLoading(false);
      setLoadingFinancial(false);
    }
  }, [diagnosticId, navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const downloadFinancialFile = async (fileId, originalName) => {
    try {
      const response = await axios.get(`${API}/diagnostics/${diagnosticId}/financial-assessment/files/${fileId}`, {
        withCredentials: true,
        responseType: "blob"
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", originalName || `arquivo_financeiro_${fileId}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading financial file:", error);
      toast.error("Erro ao baixar arquivo financeiro");
    }
  };

  const deleteFinancialFile = async (fileId) => {
    try {
      await axios.delete(`${API}/diagnostics/${diagnosticId}/financial-assessment/files/${fileId}`, {
        withCredentials: true
      });
      toast.success("Arquivo removido");
      fetchData();
    } catch (error) {
      console.error("Error deleting financial file:", error);
      toast.error(error.response?.data?.detail || "Erro ao excluir arquivo");
    }
  };

  const reanalyzeFinancial = async () => {
    setReanalyzingFinancial(true);
    try {
      await axios.post(`${API}/diagnostics/${diagnosticId}/financial-assessment/reanalyze`, {}, {
        withCredentials: true
      });
      toast.success("Reanálise concluída");
      fetchData();
    } catch (error) {
      console.error("Error reanalyzing financial:", error);
      toast.error(error.response?.data?.detail || "Erro ao reanalisar");
    } finally {
      setReanalyzingFinancial(false);
    }
  };

  const downloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const response = await axios.get(`${API}/diagnostics/${diagnosticId}/pdf`, {
        withCredentials: true,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `diagnostico_${diagnosticId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success("PDF baixado!");
    } catch (error) {
      console.error("Error downloading PDF:", error);
      toast.error("Erro ao baixar PDF");
    } finally {
      setDownloadingPdf(false);
    }
  };

  const downloadTxt = async () => {
    setDownloadingTxt(true);
    try {
      const response = await axios.get(`${API}/diagnostics/${diagnosticId}/txt`, {
        withCredentials: true,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `diagnostico_${diagnosticId}.txt`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success("TXT baixado!");
    } catch (error) {
      console.error("Error downloading TXT:", error);
      toast.error("Erro ao baixar TXT");
    } finally {
      setDownloadingTxt(false);
    }
  };

  const fetchAiTips = async () => {
    setLoadingTips(true);
    try {
      const response = await axios.post(`${API}/diagnostics/${diagnosticId}/ai-tips`, {}, { withCredentials: true });
      setAiTips(response.data);
      toast.success("Dicas geradas com sucesso!");
    } catch (error) {
      console.error("Error fetching AI tips:", error);
      toast.error("Erro ao gerar dicas");
    } finally {
      setLoadingTips(false);
    }
  };

  const handleSaveAccountData = async () => {
    setSavingAccount(true);
    try {
      const payload = {
        company_name: accountForm.company_name,
        company_cnpj: accountForm.company_cnpj,
        company_segment: accountForm.company_segment,
        company_size: accountForm.company_size,
        user_name: accountForm.user_name,
        user_email: accountForm.user_email
      };
      if (accountForm.user_password?.trim()) {
        payload.user_password = accountForm.user_password.trim();
      }

      const response = await axios.put(`${API}/diagnostics/${diagnosticId}/account`, payload, { withCredentials: true });
      setAccountData({
        diagnostic_id: diagnosticId,
        company: response.data.company,
        user: response.data.user
      });
      setAccountForm((prev) => ({ ...prev, user_password: "" }));
      toast.success("Dados da empresa atualizados");
      setShowAccountDialog(false);
    } catch (error) {
      console.error("Error updating company account data:", error);
      toast.error(error.response?.data?.detail || "Erro ao atualizar dados da empresa");
    } finally {
      setSavingAccount(false);
    }
  };

  const getReferralAreas = () => {
    if (!results?.areas_scores) return [];
    return Object.values(results.areas_scores)
      .filter((a) => a.score > 0)
      .sort((a, b) => a.score - b.score)
      .map((a) => a.area);
  };

  const getConsultantsForAreaAndCity = (area, city = "all") => {
    const normalize = (value) =>
      (value || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .trim()
        .toLowerCase();
    const normalizedArea = normalize(area);
    const normalizedCity = normalize(city);
    const areaConsultants = area
      ? consultants.filter((c) =>
          (c.areas_supported || []).some((supportedArea) => normalize(supportedArea) === normalizedArea)
        )
      : consultants;

    if (city === "all") return areaConsultants;
    if (city === "online") return areaConsultants.filter((c) => c.online_only);
    return areaConsultants.filter((c) => normalize(c.city) === normalizedCity);
  };

  const getAvailableCities = (area) => {
    const normalize = (value) =>
      (value || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .trim()
        .toLowerCase();
    const normalizedArea = normalize(area);
    const areaConsultants = area
      ? consultants.filter((c) =>
          (c.areas_supported || []).some((supportedArea) => normalize(supportedArea) === normalizedArea)
        )
      : consultants;
    const citySet = new Set(areaConsultants.map((c) => (c.city || "").trim()).filter(Boolean));
    const cities = Array.from(citySet).sort((a, b) => a.localeCompare(b, "pt-BR"));
    const hasOnlineOnly = areaConsultants.some((c) => c.online_only);
    if (hasOnlineOnly) {
      cities.push("Online");
    }
    return cities;
  };

  const assignConsultancy = async () => {
    if (!referralArea) {
      toast.error("Selecione uma área para direcionamento");
      return;
    }
    if (!referralConsultant) {
      toast.error("Selecione um consultor");
      return;
    }

    setAssigning(true);
    try {
      await axios.post(`${API}/referrals`, {
        diagnostic_id: diagnosticId,
        area: referralArea,
        consultant_id: referralConsultant
      }, { withCredentials: true });

      toast.success("Consultoria direcionada com sucesso!");
      fetchData();
    } catch (error) {
      console.error("Error assigning consultancy:", error);
      toast.error(error.response?.data?.detail || "Erro ao direcionar consultoria");
    } finally {
      setAssigning(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 7) return "text-emerald-600";
    if (score >= 5) return "text-amber-600";
    return "text-red-600";
  };

  const getScoreBgColor = (score) => {
    if (score >= 7) return "bg-emerald-100 text-emerald-800";
    if (score >= 5) return "bg-amber-100 text-amber-800";
    return "bg-red-100 text-red-800";
  };

  const getReferralMeta = (status) => REFERRAL_STATUS_META[status] || { label: status || "N/A", className: "bg-slate-100 text-slate-700" };

  const getAreaReferrals = (areaName) => (results?.referrals || []).filter((referral) => referral.area === areaName);
  const hasClientRequestForArea = (areaName) => getAreaReferrals(areaName).some((referral) => referral.source === "client_request");

  const getAnswerLabel = (option) => {
    const labels = {
      "A": "Excelente",
      "B": "Bom",
      "C": "Satisfatório",
      "D": "Insuficiente",
      "E": "Inadequado",
      "NA": "Não se aplica"
    };
    return labels[option] || option;
  };

  const getAnswerColor = (option) => {
    const colors = {
      "A": "bg-emerald-100 text-emerald-800",
      "B": "bg-blue-100 text-blue-800",
      "C": "bg-amber-100 text-amber-800",
      "D": "bg-orange-100 text-orange-800",
      "E": "bg-red-100 text-red-800",
      "NA": "bg-slate-100 text-slate-600"
    };
    return colors[option] || "bg-slate-100";
  };

  const getImprovementFocus = (area) => {
    if (!area) return "Mapeie processos, indicadores e responsabilidades para estruturar a evolução da área.";
    if (area.score < 5) return "Prioridade alta: revisar processos críticos, definir plano de ação semanal e acompanhar indicadores-chave.";
    if (area.score < 7) return "Prioridade média: padronizar rotinas, reforçar controles e executar melhorias contínuas.";
    return "Manter desempenho: consolidar boas práticas e monitorar para evitar regressões.";
  };

  // Custom radar with colors
  const CustomRadarChart = ({ data }) => {
    const coloredData = data.map(item => ({
      ...item,
      fill: item.score >= 7 ? "#10b981" : item.score >= 5 ? "#f59e0b" : "#ef4444"
    }));

    return (
      <ResponsiveContainer width="100%" height={350}>
        <RadarChart data={coloredData}>
          <PolarGrid stroke="#E2E8F0" />
          <PolarAngleAxis
            dataKey="shortArea"
            tick={{ fill: "#334155", fontSize: 11, fontWeight: 500 }}
          />
          <PolarRadiusAxis 
            angle={30} 
            domain={[0, 10]} 
            tick={{ fill: '#94a3b8', fontSize: 10 }}
          />
          <Radar
            name="Nota"
            dataKey="score"
            stroke="#001F3F"
            fill="#001F3F"
            fillOpacity={0.2}
            strokeWidth={2}
          />
          <Tooltip 
            formatter={(value) => [`${value.toFixed(2)}/10`, 'Nota']}
            labelFormatter={(label, payload) => payload[0]?.payload?.fullArea || label}
          />
        </RadarChart>
      </ResponsiveContainer>
    );
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy"></div>
        </div>
      </AdminLayout>
    );
  }

  if (!results) return null;

  // Prepare radar data
  const radarData = results.radar_data?.map(item => ({
    ...item,
    shortArea: item.area.length > 12 ? item.area.substring(0, 12) + "..." : item.area,
    fullArea: item.area
  })) || [];

  // Group answers by area
  const answersByArea = {};
  answers.forEach(answer => {
    if (!answersByArea[answer.area]) {
      answersByArea[answer.area] = [];
    }
    answersByArea[answer.area].push(answer);
  });

  return (
    <AdminLayout>
      <div className="p-4 sm:p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <Link to="/admin" className="inline-flex items-center text-slate-600 hover:text-navy transition-colors">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Voltar ao Dashboard
          </Link>
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setShowAnswers(true)}
              data-testid="view-answers-btn"
            >
              <ClipboardList className="h-4 w-4 mr-2" />
              Ver Respostas
            </Button>
            <Button 
              variant="outline" 
              size="sm"
              onClick={downloadPdf}
              disabled={downloadingPdf}
              data-testid="download-pdf-btn"
            >
              {downloadingPdf ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              PDF
            </Button>
            <Button 
              variant="outline" 
              size="sm"
              onClick={downloadTxt}
              disabled={downloadingTxt}
              data-testid="download-txt-btn"
            >
              {downloadingTxt ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <FileText className="h-4 w-4 mr-2" />
              )}
              TXT
            </Button>
          </div>
        </div>

        {/* Title */}
        <div className="mb-10">
          <h1 className="text-3xl font-serif font-bold text-navy mb-2">
            Resultado do Diagnóstico
          </h1>
          <p className="text-slate-600">{results.company_name}</p>
        </div>

        <Card className="border-0 shadow-md mb-10">
          <CardHeader>
            <CardTitle className="font-serif text-lg flex items-center gap-2">
              <Users className="h-5 w-5 text-navy" />
              Situação dos Encaminhamentos Deste Diagnóstico
            </CardTitle>
            <CardDescription>
              Acompanhe área solicitada, origem do encaminhamento, etapa atual e consultor vinculado.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {results.referrals?.length ? (
              <div className="space-y-3">
                {results.referrals.map((referral) => {
                  const statusMeta = getReferralMeta(referral.status);
                  return (
                    <div key={referral.referral_id} className="rounded-lg border border-slate-200 bg-white p-4">
                      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                        <div className="space-y-1">
                          <p className="text-sm font-semibold text-navy">{referral.area}</p>
                          <p className="text-xs text-slate-500">
                            {REFERRAL_SOURCE_LABELS[referral.source] || "Encaminhamento vinculado ao diagnóstico"}
                          </p>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <Badge className={statusMeta.className}>{statusMeta.label}</Badge>
                          <Badge variant="outline">
                            {referral.consultant_name ? `Consultor: ${referral.consultant_name}` : "Consultor ainda não definido"}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-slate-500">
                Este diagnóstico ainda não possui solicitação ou encaminhamento de consultoria.
              </p>
            )}
          </CardContent>
        </Card>

        <Card className="border-0 shadow-md mb-10">
          <CardHeader>
            <CardTitle className="font-serif text-lg">Dados de Acesso da Empresa</CardTitle>
            <CardDescription>Login usado para responder o diagnóstico e dados cadastrais da empresa</CardDescription>
          </CardHeader>
          <CardContent>
            {!accountData ? (
              <p className="text-sm text-slate-500">Dados da conta não disponíveis.</p>
            ) : (
              <div className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="rounded border p-3 bg-slate-50">
                    <p className="text-xs text-slate-500 mb-1">Login (e-mail)</p>
                    <p className="font-medium text-navy">{accountData.user?.email || "N/A"}</p>
                  </div>
                  <div className="rounded border p-3 bg-slate-50">
                    <p className="text-xs text-slate-500 mb-1">Nome do Usuário</p>
                    <p className="font-medium text-navy">{accountData.user?.name || "N/A"}</p>
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="rounded border p-3 bg-slate-50">
                    <p className="text-xs text-slate-500 mb-1">Empresa</p>
                    <p className="font-medium text-navy">{accountData.company?.name || "N/A"}</p>
                  </div>
                  <div className="rounded border p-3 bg-slate-50">
                    <p className="text-xs text-slate-500 mb-1">CNPJ</p>
                    <p className="font-medium text-navy">{accountData.company?.cnpj || "Não informado"}</p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  onClick={() => setShowAccountDialog(true)}
                  data-testid="admin-edit-company-account-btn"
                >
                  Editar Dados Cadastrais
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Overall Score */}
        <div className="grid lg:grid-cols-3 gap-8 mb-10">
          <Card className="lg:col-span-1 border-0 shadow-lg">
            <CardContent className="p-6 sm:p-8 text-center">
              <h2 className="text-sm font-medium text-slate-600 mb-4">Índice Geral de Gestão</h2>
              <div className={`text-6xl font-serif font-bold mb-2 ${getScoreColor(results.overall_percent / 10)}`}>
                {results.overall_percent?.toFixed(0)}%
              </div>
              <Badge className={`text-base px-4 py-1 ${getScoreBgColor(results.overall_score)}`}>
                {results.overall_band}
              </Badge>
              <p className="text-sm text-slate-500 mt-4">
                Nota: {results.overall_score?.toFixed(2)}/10
              </p>

              <Separator className="my-5" />
              <div className="text-left space-y-3">
                <p className="text-xs font-medium text-slate-600">
                  Direcionar consultoria desta empresa
                </p>

                <Select
                  value={referralArea}
                  onValueChange={(value) => {
                    setReferralArea(value);
                    setReferralCity("all");
                    setReferralConsultant("");
                  }}
                >
                  <SelectTrigger data-testid="admin-referral-area-select">
                    <SelectValue placeholder="Selecione a área" />
                  </SelectTrigger>
                  <SelectContent>
                    {getReferralAreas().map((area) => (
                      <SelectItem key={area} value={area}>
                        {area}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select
                  value={referralCity}
                  onValueChange={(value) => {
                    setReferralCity(value);
                    setReferralConsultant("");
                  }}
                >
                  <SelectTrigger data-testid="admin-referral-city-select">
                    <SelectValue placeholder="Filtrar por cidade" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas as cidades</SelectItem>
                    {getAvailableCities(referralArea).map((city) => (
                      <SelectItem key={city} value={city === "Online" ? "online" : city}>
                        {city}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={referralConsultant} onValueChange={setReferralConsultant}>
                  <SelectTrigger data-testid="admin-referral-consultant-select">
                    <SelectValue placeholder="Selecione o consultor" />
                  </SelectTrigger>
                  <SelectContent>
                    {getConsultantsForAreaAndCity(referralArea, referralCity).map((consultant) => (
                      <SelectItem key={consultant.consultant_id} value={consultant.consultant_id}>
                        {consultant.user_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Button
                  onClick={assignConsultancy}
                  disabled={assigning}
                  className="w-full bg-navy hover:bg-navy/90"
                  data-testid="admin-assign-consultancy-btn"
                >
                  {assigning ? "Direcionando..." : "Direcionar para Consultor"}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Radar Chart */}
          <Card className="lg:col-span-2 border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="font-serif text-lg">Visão Geral por Área</CardTitle>
              <CardDescription>Verde (≥7), Amarelo (5-6.99), Vermelho (&lt;5)</CardDescription>
            </CardHeader>
            <CardContent>
              {radarData?.length ? (
                <CustomRadarChart data={radarData} />
              ) : (
                <div className="rounded-sm bg-slate-50 p-6 text-sm text-slate-600">
                  Não há áreas aplicáveis suficientes para exibir o gráfico deste diagnóstico.
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Strengths and Critical Areas */}
        <div className="grid md:grid-cols-2 gap-8 mb-10">
          {/* Strengths */}
          <Card className="border-0 shadow-md">
            <CardHeader className="pb-2">
              <CardTitle className="font-serif text-lg flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-emerald-600" />
                Pontos Fortes
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {results.strengths?.slice(0, 3).map((area, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-emerald-50 rounded-sm">
                    <span className="text-sm font-medium text-slate-700">{area.area}</span>
                    <Badge className="bg-emerald-100 text-emerald-800">{area.score?.toFixed(2)}</Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Critical Areas */}
          <Card className="border-0 shadow-md">
            <CardHeader className="pb-2">
              <CardTitle className="font-serif text-lg flex items-center gap-2">
                <TrendingDown className="h-5 w-5 text-red-600" />
                Áreas Críticas
              </CardTitle>
            </CardHeader>
            <CardContent>
              {results.critical_areas?.length ? (
                <div className="space-y-3">
                  {results.critical_areas?.slice(0, 3).map((area, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-red-50 rounded-sm">
                      <div>
                        <span className="text-sm font-medium text-slate-700">{area.area}</span>
                        {!!getAreaReferrals(area.area).length && (
                          <div className="mt-1 flex flex-wrap gap-1">
                            {getAreaReferrals(area.area).map((referral) => {
                              const statusMeta = getReferralMeta(referral.status);
                              return (
                                <Badge key={referral.referral_id} className={statusMeta.className}>
                                  {statusMeta.label}
                                  {referral.consultant_name ? ` • ${referral.consultant_name}` : ""}
                                </Badge>
                              );
                            })}
                          </div>
                        )}
                      </div>
                      <Badge className="bg-red-100 text-red-800">{area.score?.toFixed(2)}</Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="rounded-sm bg-emerald-50 p-3 text-sm text-emerald-800">
                  Nenhuma área crítica identificada. Só entram aqui áreas com nota abaixo de 7.
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <Card className="border-0 shadow-md mb-10">
          <CardHeader>
            <CardTitle className="font-serif text-lg flex items-center gap-2">
              <Landmark className="h-5 w-5 text-navy" />
              Avaliação Financeira (DRE/Balanço)
            </CardTitle>
            <CardDescription>Apontamento financeiro quando a empresa envia relatórios para análise por IA</CardDescription>
          </CardHeader>
          <CardContent>
            {loadingFinancial ? (
              <p className="text-sm text-slate-500">Carregando avaliação financeira...</p>
            ) : !financialAssessment ? (
              <p className="text-sm text-slate-500">Este cliente ainda não enviou DRE/Balanço para avaliação financeira.</p>
            ) : (
              <div className="space-y-4">
                <div className="grid gap-3 md:grid-cols-3">
                  <div className="rounded-lg border border-slate-200 bg-white p-4">
                    <p className="text-xs uppercase tracking-wide text-slate-500">Estado financeiro</p>
                    <Badge
                      className={`mt-2 ${
                        financialAssessment.estado_financeiro === "Saudável"
                          ? "bg-emerald-100 text-emerald-800"
                          : financialAssessment.estado_financeiro === "Atenção"
                          ? "bg-amber-100 text-amber-800"
                          : financialAssessment.estado_financeiro === "Crítico"
                          ? "bg-red-100 text-red-800"
                          : "bg-slate-100 text-slate-700"
                      }`}
                    >
                      {financialAssessment.estado_financeiro || "Indeterminado"}
                    </Badge>
                  </div>
                  <div className="rounded-lg border border-slate-200 bg-white p-4">
                    <p className="text-xs uppercase tracking-wide text-slate-500">Leitura priorizada</p>
                    <p className="mt-2 text-sm font-semibold text-navy">{financialAssessment.tipo_analise || "Financeira"}</p>
                  </div>
                  <div className="rounded-lg border border-slate-200 bg-white p-4">
                    <p className="text-xs uppercase tracking-wide text-slate-500">Quantidade de arquivos</p>
                    <p className="mt-2 text-sm font-semibold text-navy">{(financialAssessment.files || []).length} arquivo(s)</p>
                  </div>
                </div>
                <div className="rounded-lg border border-slate-200 bg-white p-4">
                  <h4 className="font-medium text-navy mb-2">Visão Executiva</h4>
                  <p className="text-sm text-slate-700 whitespace-pre-line">
                    {financialAssessment.resumo_executivo || financialAssessment.resumo}
                  </p>
                </div>
                {!!financialAssessment.leitura_empresarial?.length && (
                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                    <h4 className="font-medium text-navy mb-2">O que isso significa para a empresa</h4>
                    <ul className="list-disc pl-5 text-sm text-slate-700 space-y-1">
                      {financialAssessment.leitura_empresarial.map((item, idx) => (
                        <li key={`admin-fin-business-read-${idx}`}>{item}</li>
                      ))}
                    </ul>
                  </div>
                )}
                <div>
                  <h4 className="font-medium text-navy mb-1">Leitura Técnica Resumida</h4>
                  <p className="text-sm text-slate-600 whitespace-pre-line">{financialAssessment.resumo}</p>
                </div>
                <div>
                  <h4 className="font-medium text-navy mb-2">Índices de Liquidez e Estrutura</h4>
                  <FinancialIndexCards items={financialAssessment.indices_financeiros || []} prefix="fin-index-admin" />
                </div>
                <div>
                  <h4 className="font-medium text-navy mb-1">Dicas Primárias</h4>
                  <ul className="list-disc pl-5 text-sm text-slate-600 space-y-1">
                    {(financialAssessment.dicas_primarias || []).map((tip, idx) => (
                      <li key={`admin-fin-tip-${idx}`}>{tip}</li>
                    ))}
                  </ul>
                </div>
                <div className="rounded border border-blue-100 bg-blue-50 p-3">
                  <p className="text-sm text-blue-900">{financialAssessment.recomendacao_consultoria}</p>
                </div>
                <div>
                  <h4 className="font-medium text-navy mb-2">Arquivos Enviados</h4>
                  <Button
                    variant="outline"
                    size="sm"
                    className="mb-2"
                    onClick={reanalyzeFinancial}
                    disabled={reanalyzingFinancial}
                  >
                    {reanalyzingFinancial ? "Reanalisando..." : "Reanalisar IA"}
                  </Button>
                  <div className="space-y-2">
                    {(financialAssessment.files || []).map((file) => (
                      <div key={file.file_id} className="flex items-center justify-between rounded border border-slate-200 bg-white p-2">
                        <div>
                          <p className="text-sm text-slate-700">{file.original_name}</p>
                          <p className="text-xs text-slate-500">{Math.round((file.size_bytes || 0) / 1024)} KB</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button variant="outline" size="sm" onClick={() => downloadFinancialFile(file.file_id, file.original_name)}>
                            <FileDown className="h-4 w-4 mr-1" />
                            Baixar
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => deleteFinancialFile(file.file_id)}>
                            <Trash2 className="h-4 w-4 mr-1" />
                            Excluir
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* All Areas Scores */}
        <Card className="border-0 shadow-md mb-10">
          <CardHeader>
            <CardTitle className="font-serif text-lg">Notas por Área</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.values(results.areas_scores || {})
                .sort((a, b) => a.score - b.score)
                .map((area) => (
                <div 
                  key={area.area} 
                  className={`p-4 border-l-4 rounded-sm cursor-pointer transition hover:shadow-sm ${
                    area.score >= 7 ? "border-l-emerald-500 bg-emerald-50" :
                    area.score >= 5 ? "border-l-amber-500 bg-amber-50" :
                    "border-l-red-500 bg-red-50"
                  }`}
                  onClick={() => {
                    setSelectedArea(area);
                    setShowAreaDetail(true);
                  }}
                  data-testid={`admin-area-detail-${area.area}`}
                >
                  <div className="flex items-center justify-between mb-2 gap-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium text-sm text-navy">{area.area}</span>
                      {hasClientRequestForArea(area.area) && (
                        <Badge className="bg-sky-100 text-sky-800">Pedido do cliente</Badge>
                      )}
                    </div>
                    <Badge className={getScoreBgColor(area.score)}>
                      {area.score?.toFixed(2)}
                    </Badge>
                  </div>
                  <p className="text-xs text-slate-500">{area.band}</p>
                  {!!getAreaReferrals(area.area).length && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {getAreaReferrals(area.area).map((referral) => {
                        const statusMeta = getReferralMeta(referral.status);
                        return (
                          <Badge key={referral.referral_id} className={statusMeta.className}>
                            {statusMeta.label}
                            {referral.consultant_name ? ` • ${referral.consultant_name}` : ""}
                          </Badge>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>
            <p className="text-xs text-slate-500 mt-4">Clique em uma área para ver lacunas detalhadas e foco de melhoria.</p>
          </CardContent>
        </Card>

        {/* AI Tips Section */}
        <Card className="border-0 shadow-md mb-10">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="font-serif text-lg flex items-center gap-2">
                  <Lightbulb className="h-5 w-5 text-gold" />
                  Dicas e Recomendações
                </CardTitle>
                <CardDescription>Sugestões geradas por IA para melhorar a gestão do cliente</CardDescription>
              </div>
              {!aiTips && (
                <Button
                  onClick={fetchAiTips}
                  disabled={loadingTips}
                  className="bg-navy hover:bg-navy/90"
                  data-testid="generate-tips-btn-admin"
                >
                  {loadingTips ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Gerando...
                    </>
                  ) : (
                    <>
                      <Lightbulb className="h-4 w-4 mr-2" />
                      Gerar Dicas
                    </>
                  )}
                </Button>
              )}
            </div>
          </CardHeader>
          {aiTips && (
            <CardContent>
              <div className="space-y-6">
                {Object.entries(aiTips.tips || {}).map(([area, tips]) => (
                  <div key={area}>
                    <h4 className="font-medium text-navy mb-2">{area}</h4>
                    <p className="text-sm text-slate-600 whitespace-pre-line">{tips}</p>
                  </div>
                ))}

                {aiTips.action_plan && (
                  <>
                    <Separator />
                    <div>
                      <h4 className="font-medium text-navy mb-2">Plano de Ação Sugerido (30 dias)</h4>
                      <p className="text-sm text-slate-600 whitespace-pre-line">{aiTips.action_plan}</p>
                    </div>
                  </>
                )}
              </div>
            </CardContent>
          )}
        </Card>

        {/* Answers Dialog */}
        <Dialog open={showAnswers} onOpenChange={setShowAnswers}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-serif">Respostas do Formulário</DialogTitle>
            </DialogHeader>
            <div className="mt-4 space-y-6">
              {Object.entries(answersByArea).map(([area, areaAnswers]) => (
                <div key={area}>
                  <h3 className="font-serif font-semibold text-navy mb-3 pb-2 border-b">{area}</h3>
                  <div className="space-y-3">
                    {areaAnswers.map(answer => {
                      const question = questions.find(q => q.id === answer.question_id);
                      return (
                        <div key={answer.question_id} className="p-3 bg-slate-50 rounded-sm">
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                              <p className="text-sm text-slate-600 mb-1">
                                <span className="font-mono text-xs text-slate-400">#{answer.question_id}</span>
                              </p>
                              <p className="text-sm text-navy">{question?.text || "Pergunta não encontrada"}</p>
                              {answer.note_text && (
                                <p className="text-xs text-slate-500 mt-2 italic">
                                  Obs: {answer.note_text}
                                </p>
                              )}
                            </div>
                            <Badge className={getAnswerColor(answer.answer_option)}>
                              {answer.answer_option} - {getAnswerLabel(answer.answer_option)}
                            </Badge>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </DialogContent>
        </Dialog>

        <Dialog open={showAreaDetail} onOpenChange={setShowAreaDetail}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-serif">{selectedArea?.area || "Detalhes da Área"}</DialogTitle>
            </DialogHeader>
            {selectedArea && (
              <div className="space-y-3 text-sm">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge className={getScoreBgColor(selectedArea.score)}>
                    Nota {selectedArea.score?.toFixed(2)} - {selectedArea.band}
                  </Badge>
                  {hasClientRequestForArea(selectedArea.area) && (
                    <Badge className="bg-sky-100 text-sky-800">Área solicitada pela empresa</Badge>
                  )}
                </div>
                <p className="text-slate-700">
                  <span className="font-medium text-navy">Lacunas identificadas: </span>
                  {selectedArea.narrative || "Não há narrativa detalhada para esta área."}
                </p>
                <p className="text-slate-700">
                  <span className="font-medium text-navy">Cobertura do diagnóstico: </span>
                  {selectedArea.answered_count ?? 0} de {selectedArea.questions_count ?? 0} perguntas respondidas.
                </p>
                <p className="text-slate-700">
                  <span className="font-medium text-navy">Foco recomendado: </span>
                  {getImprovementFocus(selectedArea)}
                </p>
              </div>
            )}
          </DialogContent>
        </Dialog>

        <Dialog open={showAccountDialog} onOpenChange={setShowAccountDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-serif">Editar Dados da Empresa</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div className="space-y-2">
                <Label>Nome da Empresa</Label>
                <Input
                  value={accountForm.company_name}
                  onChange={(e) => setAccountForm((prev) => ({ ...prev, company_name: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>CNPJ</Label>
                <Input
                  value={accountForm.company_cnpj}
                  onChange={(e) => setAccountForm((prev) => ({ ...prev, company_cnpj: e.target.value }))}
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Segmento</Label>
                  <Input
                    value={accountForm.company_segment}
                    onChange={(e) => setAccountForm((prev) => ({ ...prev, company_segment: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Porte</Label>
                  <Input
                    value={accountForm.company_size}
                    onChange={(e) => setAccountForm((prev) => ({ ...prev, company_size: e.target.value }))}
                  />
                </div>
              </div>
              <Separator />
              <div className="space-y-2">
                <Label>Nome do Usuário</Label>
                <Input
                  value={accountForm.user_name}
                  onChange={(e) => setAccountForm((prev) => ({ ...prev, user_name: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>E-mail (Login)</Label>
                <Input
                  type="email"
                  value={accountForm.user_email}
                  onChange={(e) => setAccountForm((prev) => ({ ...prev, user_email: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Nova Senha (opcional)</Label>
                <Input
                  type="password"
                  placeholder="Preencha apenas se quiser alterar"
                  value={accountForm.user_password}
                  onChange={(e) => setAccountForm((prev) => ({ ...prev, user_password: e.target.value }))}
                />
              </div>
              <Button
                className="w-full bg-navy hover:bg-navy/90"
                onClick={handleSaveAccountData}
                disabled={savingAccount}
                data-testid="admin-save-company-account-btn"
              >
                {savingAccount ? "Salvando..." : "Salvar Alterações"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminResults;
